import { customElement, query } from 'lit/decorators.js';
import { WEB_COMPONENT_POLICY_PANEL_EXCHANGE_ANTIMALWARE_SCAN_TASKS_GRID } from '../../../../../../../../webcomponents.constants';
import { GzPolicyUiSettingsGridWebComponent } from '../../../../../../../common/policyPanel/uiSettings/gridTemplate/gridComponents/gridView';
import {
  Column,
  ColumnKeys,
  FILTERS,
  FILTERS_UI_CONFIGS,
  getGridColumnDetails,
  GRID_COLUMNS,
  nameCellCustomRenderer,
  PREDEFINED_FILTERS_KEYS,
  recurrenceCellCustomRenderer,
} from './grid-config';
import { html, TemplateResult } from 'lit';
import { translateFn } from '../../../../../../../../utils/translator.helper';
import { GzExchangeAntimalwareScanTasksGridDataProvider } from './GzExchangeAntimalwareScanTasksGridDataProvider';
import {
  FullPagePopupConfigs,
  GzPolicyFullPageWindow,
} from '../../../../../../../common/policyPanel/uiSettings/dialogs/fullPage/GzPolicyFullPageWindow';
import { GzButtonWebComponent } from '../../../../../../../../ui/gzButton';
import { GzExchangeAntimalwareScanTasksFormWebComponent } from './rulesItemForm';
import { ValidationResult } from '../../../../../../../../ui/gzForm/formField/types/form-validator';
import { validateDuplicates } from './utils/validators';
import { ButtonColors, ButtonFontSizes, ButtonHeights, ButtonTypes } from '../../../../../../../../ui/gzButton/types';
import { FontSizes, FontWeights } from '../../../../../../../../types/common/types';
import { ExchangeAntimalwareScanTask } from '@nirvana/shared/dtos/types/policies/common';
import { ExchangeAntimalwareScanTaskFormValue } from '../../../../../utils/uiSettings/exchange/exchange-protection.types';

@customElement(WEB_COMPONENT_POLICY_PANEL_EXCHANGE_ANTIMALWARE_SCAN_TASKS_GRID)
export class GzExchangeAntimalwareScanTasksGridWebComponent extends GzPolicyUiSettingsGridWebComponent<
  Column,
  ColumnKeys,
  ExchangeAntimalwareScanTask,
  ExchangeAntimalwareScanTaskFormValue,
  GzExchangeAntimalwareScanTasksGridDataProvider
> {
  protected allColumnDetails = getGridColumnDetails();
  protected columns = GRID_COLUMNS;
  protected columnRenderers = {
    [ColumnKeys.NAME]: nameCellCustomRenderer.bind(null, this.onRuleNameClick.bind(this)),
    [ColumnKeys.RECURRENCE]: recurrenceCellCustomRenderer,
  };
  protected showHeaderCustomActionsInsideGrid = true;

  protected hasFilters = true;
  protected filters = FILTERS;
  protected predefinedFiltersKeys = PREDEFINED_FILTERS_KEYS;
  protected filtersUiConfigs = FILTERS_UI_CONFIGS;

  protected hasSelectableRows = true;
  protected hasEditRowAction = false;
  protected hasBasicEditRowAction = true;

  protected translations = {
    editRow: translateFn('Edit'),
    deleteRow: translateFn('Delete'),
  };

  @query('#delete-button')
  protected deleteButton: GzButtonWebComponent;

  protected getDataProvider(): GzExchangeAntimalwareScanTasksGridDataProvider {
    return new GzExchangeAntimalwareScanTasksGridDataProvider(this.initialGridItems);
  }

  protected renderEmptyStateGrid(): TemplateResult | null {
    return html`
      <gz-text fontSize="${FontSizes.HEADING_6}" fontWeight="${FontWeights.LIGHT}">${translateFn('No rules')}</gz-text>

      <gz-button
        id="empty-state-add-button"
        type="${ButtonTypes.QUIET}"
        buttonHeight="${ButtonHeights.SMALL}"
        buttonFontSize="${ButtonFontSizes.SMALL}"
        style="margin-top: 5px;"
        @click="${this.onAddButtonClick.bind(this)}"
        >${translateFn('Adjust your filters or start adding rules.')}
      </gz-button>
    `;
  }

  protected renderHeaderButtons(): TemplateResult {
    return html`
      <gz-button id="add-button" type="${ButtonTypes.OUTLINE}" @click="${this.onAddButtonClick.bind(this)}"
        >${translateFn('Add')}
      </gz-button>

      <gz-button
        id="delete-button"
        type="${ButtonTypes.OUTLINE}"
        color="${ButtonColors.NEGATIVE}"
        ?disabled
        @click="${this.onDeleteSelectedItemsWithDialog.bind(
          this,
          translateFn('Delete scan tasks?'),
          translateFn('You are about to delete the selected scan tasks. This action cannot be undone.')
        )}"
        >${translateFn('Delete')}
      </gz-button>
    `;
  }

  protected validateGridItemBeforeAdd(gridItemFormValue: ExchangeAntimalwareScanTaskFormValue): ValidationResult {
    return validateDuplicates(
      gridItemFormValue,
      (this.gridComponent.dataProvider as GzExchangeAntimalwareScanTasksGridDataProvider).items
    );
  }

  protected validateGridItemBeforeEdit(
    oldItem: ExchangeAntimalwareScanTaskFormValue,
    newItem: ExchangeAntimalwareScanTaskFormValue
  ): ValidationResult {
    // skip if item name is not changed
    if (newItem.name === oldItem.name) {
      return { valid: true };
    }

    return this.validateGridItemBeforeAdd(newItem);
  }

  protected onSelectRows(event: CustomEvent) {
    super.onSelectRows(event);
    this.deleteButton.disabled = !event.detail.selectionStoreData.length;
  }

  private onAddButtonClick(): void {
    const addRuleCallback = (formValue: ExchangeAntimalwareScanTaskFormValue): ValidationResult =>
      this.onNewGridItemAdd(formValue, formValue.defaultPositionInGrid);

    // create configs
    const configs: FullPagePopupConfigs = {
      bodyForm: new GzExchangeAntimalwareScanTasksFormWebComponent(),
      strings: {
        title: translateFn('Add scan task'),
        confirm: translateFn('Add'),
        cancel: translateFn('Cancel'),
        cancelConfirmation: {
          title: translateFn('Discard changes?'),
          body: translateFn('Changes you have made will be lost.'),
          cancel: translateFn('Keep changes'),
          confirm: translateFn('Discard'),
        },
      },
      confirmCallbackFn: addRuleCallback,
      shouldShowCancelConfirmation: true,
    };

    // show window
    const window = new GzPolicyFullPageWindow(configs);
    window.show();
  }

  private onRuleNameClick(item: ExchangeAntimalwareScanTaskFormValue): void {
    const addRuleCallback = (formValue: ExchangeAntimalwareScanTaskFormValue): ValidationResult =>
      this.onEditGridItemFromPopup(formValue, item);

    // create configs
    const configs: FullPagePopupConfigs = {
      bodyForm: new GzExchangeAntimalwareScanTasksFormWebComponent(item),
      strings: {
        title: translateFn('Edit scan task'),
        confirm: translateFn('Save#preserve state'),
        cancel: translateFn('Cancel'),
        cancelConfirmation: {
          title: translateFn('Discard changes?'),
          body: translateFn('Changes you have made will be lost.'),
          cancel: translateFn('Keep changes'),
          confirm: translateFn('Discard'),
        },
      },
      confirmCallbackFn: addRuleCallback,
      shouldShowCancelConfirmation: true,
    };
    // show window
    const window = new GzPolicyFullPageWindow(configs);
    window.show();
  }

  protected onBasicEditRow(event: CustomEvent) {
    this.onRuleNameClick(event.detail.rowData.item);
  }
}
